/**
 * Sawyer E4 (#754) — one direct thread between two staffers.
 *
 * Oldest first; mine on the right, theirs on the left with their name on
 * the first of a run. The composer is pinned to the bottom (Send is the one
 * primary button; Enter sends on desktop, Shift+Enter is a newline; on a
 * touch device Enter is a newline). Polls every 15s while open and visible
 * (the Conversations inbox's cadence) and marks the thread read up to the
 * newest message SHOWN. When the other person no longer has an account the
 * composer is replaced by that sentence (AC11).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  personName,
  roleLabel,
  staffMessagesApi,
  type Message,
  type Thread,
} from '../../lib/staff-messages-api.js';
import { refreshUnread } from './useStaffMessagesUnread.js';
import { bodyState, Composer, LoadError, SkeletonRows, StaleLine, TimeStamp, useVisiblePoll } from './shared.js';

export const THREAD_POLL_MS = 15_000;

type Pending = { tempId: string; body: string; status: 'sending' | 'failed' };
type Load = { kind: 'loading' } | { kind: 'error' } | { kind: 'missing' } | { kind: 'ready'; thread: Thread; messages: Message[] };

export interface ThreadViewProps {
  apiUrl: string;
  threadId: string;
  canWrite: boolean;
  /** Tell the list a message moved (its preview / order / unread changed). */
  onActivity?: () => void;
}

export function inactiveSentence(firstName: string): string {
  return `${firstName.trim() || 'This person'} no longer has an active account here.`;
}

export function ThreadView({ apiUrl, threadId, canWrite, onActivity }: ThreadViewProps): JSX.Element {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [pending, setPending] = useState<Pending[]>([]);
  const [draft, setDraft] = useState('');
  const [stale, setStale] = useState(false);
  const [lastGood, setLastGood] = useState<Date | null>(null);
  const markedUpTo = useRef<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  // Held in a ref so a parent re-render never re-runs the load effect.
  const activityRef = useRef(onActivity);
  activityRef.current = onActivity;

  const markRead = useCallback(
    (messages: Message[]) => {
      const newest = messages.at(-1)?.createdAt;
      if (!newest || newest === markedUpTo.current) return;
      markedUpTo.current = newest;
      void staffMessagesApi.markThreadRead(apiUrl, threadId, newest).then(() => {
        void refreshUnread();
        activityRef.current?.();
      });
    },
    [apiUrl, threadId],
  );

  const loadThread = useCallback(
    async (mode: 'first' | 'poll') => {
      if (mode === 'first') setLoad({ kind: 'loading' });
      const r = await staffMessagesApi.thread(apiUrl, threadId);
      if (!r.ok) {
        if (mode === 'poll') {
          setStale(true);
          return;
        }
        setLoad(r.code === 'thread_not_found' ? { kind: 'missing' } : { kind: 'error' });
        return;
      }
      setStale(false);
      setLastGood(new Date());
      setLoad({ kind: 'ready', thread: r.data.thread, messages: r.data.messages });
      markRead(r.data.messages);
    },
    [apiUrl, threadId, markRead],
  );

  useEffect(() => {
    markedUpTo.current = null;
    setPending([]);
    setDraft('');
    void loadThread('first');
  }, [loadThread]);

  useVisiblePoll(() => void loadThread('poll'), THREAD_POLL_MS, load.kind === 'ready');

  const count = load.kind === 'ready' ? load.messages.length + pending.length : 0;
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [count]);

  async function send(body: string, tempId: string): Promise<void> {
    const r = await staffMessagesApi.send(apiUrl, threadId, body);
    if (r.ok) {
      setPending((list) => list.filter((p) => p.tempId !== tempId));
      setLoad((cur) => (cur.kind === 'ready' ? { ...cur, messages: [...cur.messages, r.data.message] } : cur));
      markedUpTo.current = r.data.message.createdAt;
      activityRef.current?.();
      return;
    }
    setPending((list) => list.map((p) => (p.tempId === tempId ? { ...p, status: 'failed' } : p)));
    setDraft((d) => (d.trim() === '' ? body : d));
    // The other person just lost their account: reload to show why.
    if (r.code === 'recipient_inactive') void loadThread('poll');
  }

  function submit(): void {
    const { trimmed, empty, over } = bodyState(draft);
    if (empty || over) return;
    const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setPending((list) => [...list, { tempId, body: trimmed, status: 'sending' }]);
    setDraft('');
    void send(trimmed, tempId);
  }

  function retry(p: Pending): void {
    setPending((list) => list.map((x) => (x.tempId === p.tempId ? { ...x, status: 'sending' } : x)));
    if (draft.trim() === p.body) setDraft('');
    void send(p.body, p.tempId);
  }

  const other = load.kind === 'ready' ? load.thread.other : null;

  return (
    <section className="ferro-tm-thread" data-testid="team-messages-thread" aria-label="Conversation">
      <div className="ferro-tm-thread__head">
        <Link to="/team-messages/direct" className="ferro-tm-back" data-testid="thread-back">
          Back to messages
        </Link>
        {other ? (
          <div className="ferro-tm-thread__who">
            <h2 className="ferro-tm-thread__name">{personName(other)}</h2>
            {roleLabel(other.role) ? <span className="ferro-tm__muted">{roleLabel(other.role)}</span> : null}
          </div>
        ) : null}
      </div>

      {stale ? <StaleLine lastGood={lastGood} /> : null}

      <div className="ferro-tm-thread__scroll">
        {load.kind === 'loading' ? <SkeletonRows count={4} variant="bubble" /> : null}
        {load.kind === 'error' ? <LoadError testId="thread-error" onRetry={() => void loadThread('first')} /> : null}
        {load.kind === 'missing' ? (
          <p className="ferro-tm-empty" data-testid="thread-missing">
            This conversation isn&apos;t available.
          </p>
        ) : null}
        {load.kind === 'ready' ? (
          <ol className="ferro-tm-bubbles" data-testid="thread-messages">
            {load.messages.map((m, i) => {
              const firstOfRun = !m.isMine && (i === 0 || load.messages[i - 1]!.isMine);
              return (
                <li
                  key={m.id}
                  className={`ferro-tm-bubble ${m.isMine ? 'ferro-tm-bubble--mine' : 'ferro-tm-bubble--theirs'}`}
                  data-testid="thread-message"
                >
                  {firstOfRun && other ? <span className="ferro-tm-bubble__name">{other.firstName}</span> : null}
                  <p className="ferro-tm-bubble__body">{m.body}</p>
                  <TimeStamp iso={m.createdAt} />
                </li>
              );
            })}
            {pending.map((p) => (
              <li key={p.tempId} className="ferro-tm-bubble ferro-tm-bubble--mine ferro-tm-bubble--pending" data-testid="thread-message-pending">
                <p className="ferro-tm-bubble__body">{p.body}</p>
                {p.status === 'sending' ? (
                  <span className="ferro-tm__time">Sending…</span>
                ) : (
                  <span className="ferro-tm-post__failed" data-testid="thread-message-failed">
                    Not sent
                    <button type="button" className="ferro-tm-link" onClick={() => retry(p)}>
                      Retry
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ol>
        ) : null}
        <div ref={endRef} />
      </div>

      {load.kind === 'ready' ? (
        other && !other.isActive ? (
          <p className="ferro-tm-readonly" data-testid="thread-inactive">
            {inactiveSentence(other.firstName)}
          </p>
        ) : canWrite ? (
          <div className="ferro-tm-thread__composer">
            <Composer
              testId="thread-composer"
              label={other ? `Message to ${other.firstName}` : 'Message'}
              placeholder={other ? `Message ${other.firstName}` : 'Message'}
              value={draft}
              onChange={setDraft}
              onSubmit={submit}
              submitLabel="Send"
              rows={2}
              enterSends
            />
          </div>
        ) : (
          <p className="ferro-tm-readonly" data-testid="thread-readonly">
            You can read this conversation. Sending messages isn&apos;t part of your role.
          </p>
        )
      ) : null}
    </section>
  );
}
